import {
  VerifiedUser,
  Warning,
  Error as ErrorIcon,
  Info,
} from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material";
import { tokens } from "@/tokens";

// Shared tab styling for consolidated page tabs (sharp corners, bottom-border indicator)
export const tabSx: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: tokens.fontWeight.bold,
  fontSize: tokens.fontSize.lg,
  minHeight: 44,
  padding: '8px 16px',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  '&.Mui-selected': {
    color: '#0a0a0f',
  },
};

// Audit status helpers (MUI semantic color — used by legacy callers)
export const getAuditStatusColor = (status: ClusterAuditStatus) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "NEEDS_REVIEW":
      return "warning";
    case "DISCREPANCIES_FOUND":
      return "error";
    default:
      return "default";
  }
};

// Muted outlined style colors
export const getAuditStatusStyle = (status: ClusterAuditStatus) => {
  switch (status) {
    case "APPROVED":
      return { color: tokens.status.success, borderColor: tokens.status.success };
    case "NEEDS_REVIEW":
      return { color: tokens.status.warning, borderColor: tokens.status.warning };
    case "DISCREPANCIES_FOUND":
      return { color: tokens.status.danger, borderColor: tokens.status.danger };
    default:
      return { color: tokens.text.secondary, borderColor: tokens.text.secondary };
  }
};

export const getAuditStatusLabel = (
  status: ClusterAuditStatus,
  short?: boolean
) => {
  switch (status) {
    case "APPROVED":
      return "Approved";
    case "NEEDS_REVIEW":
      return short ? "Review" : "Needs Review";
    case "DISCREPANCIES_FOUND":
      return short ? "Issues" : "Discrepancies Found";
    default:
      return status;
  }
};

export const getAuditStatusIcon = (
  status?: ClusterAuditStatus,
  size: number = 14
) => {
  switch (status) {
    case "APPROVED":
      return <VerifiedUser sx={{ fontSize: size }} />;
    case "NEEDS_REVIEW":
      return <Warning sx={{ fontSize: size }} />;
    case "DISCREPANCIES_FOUND":
      return <ErrorIcon sx={{ fontSize: size }} />;
    default:
      return null;
  }
};

// Discrepancy severity helpers
export const getSeverityColor = (severity: DiscrepancySeverity) => {
  switch (severity) {
    case "HIGH":
      return tokens.status.danger;
    case "MEDIUM":
      return tokens.status.warning;
    case "LOW":
      return tokens.status.info;
    case "INFO":
      return tokens.text.secondary;
    default:
      return tokens.text.secondary;
  }
};

export const getSeverityIcon = (severity: DiscrepancySeverity) => {
  switch (severity) {
    case "HIGH":
      return <ErrorIcon sx={{ fontSize: 18, color: tokens.status.danger }} />;
    case "MEDIUM":
      return <Warning sx={{ fontSize: 18, color: tokens.status.warning }} />;
    case "LOW":
      return <Info sx={{ fontSize: 18, color: tokens.status.info }} />;
    case "INFO":
      return <Info sx={{ fontSize: 18, color: tokens.text.secondary }} />;
    default:
      return <Info sx={{ fontSize: 18 }} />;
  }
};

// Discrepancy category labels
export const getCategoryLabel = (category: DiscrepancyCategory) => {
  switch (category) {
    case "VALUE_MISMATCH":
      return "Value Mismatch";
    case "QUANTITY_MISMATCH":
      return "Quantity Mismatch";
    case "HTS_CLASSIFICATION":
      return "HTS Classification";
    case "COUNTRY_OF_ORIGIN":
      return "Country of Origin";
    case "MANUFACTURER_MISMATCH":
      return "Manufacturer Mismatch";
    case "MISSING_DOCUMENT":
      return "Missing Document";
    case "DATE_DISCREPANCY":
      return "Date Discrepancy";
    case "IDENTIFIER_MISMATCH":
      return "Identifier Mismatch";
    case "DUTY_CALCULATION":
      return "Duty Calculation";
    case "OTHER":
      return "Other";
    default:
      return category;
  }
};

// Document type labels
export const getDocumentTypeLabel = (type?: CustomsDocumentType) => {
  if (!type) return "Unknown";
  switch (type) {
    case "ENTRY_SUMMARY_7501":
      return "7501";
    case "COMMERCIAL_INVOICE":
      return "Invoice";
    case "BILL_OF_LADING":
      return "B/L";
    case "PACKING_LIST":
      return "Packing";
    case "ARRIVAL_NOTICE":
      return "Arrival";
    case "ISF":
      return "ISF";
    case "OTHER":
      return "Other";
    case "MULTIPLE_DOCUMENTS_COMBINED":
      return "Multi";
    default:
      return type;
  }
};

export const getDocumentTypeFullLabel = (type?: CustomsDocumentType) => {
  if (!type) return "Unknown";
  switch (type) {
    case "ENTRY_SUMMARY_7501":
      return "Entry Summary (7501)";
    case "COMMERCIAL_INVOICE":
      return "Commercial Invoice";
    case "BILL_OF_LADING":
      return "Bill of Lading";
    case "PACKING_LIST":
      return "Packing List";
    case "ARRIVAL_NOTICE":
      return "Arrival Notice";
    case "ISF":
      return "ISF";
    case "OTHER":
      return "Other";
    case "MULTIPLE_DOCUMENTS_COMBINED":
      return "Multiple Documents Combined";
    default:
      return type;
  }
};

// Document processing status labels
export const getStatusLabel = (status: CustomsDocumentStatus) => {
  switch (status) {
    case "READY":
      return "Ready";
    case "FAILED":
      return "Failed";
    case "UPLOADED":
      return "Uploaded";
    case "TEXT_EXTRACTED":
      return "Processing...";
    case "KEYS_EXTRACTED":
      return "Clustering...";
    default:
      return status;
  }
};
